/**
 * Multi-Chain & Multi-DEX Configuration
 * 
 * This file contains all supported chains and DEXes with their respective
 * router addresses, factory addresses, and token addresses.
 */

export const CHAIN_CONFIGS = {
  cronos: {
    id: 25,
    name: "Cronos",
    nativeCurrency: {
      name: "CRO",
      symbol: "CRO",
      decimals: 18,
    },
    rpcUrls: {
      default: "https://cronos-evm-rpc.publicnode.com",
      public: "https://evm.cronos.org",
      infura: null,
      alchemy: null,
    },
    blockExplorerUrls: ["https://cronoscan.com"],
    wrappedNativeToken: "0x5C7F8A570d578ED84E63fdFA7b1eE72dEae1AE23", // WCRO
    stablecoins: {
      USDC: "0xc21223249CA28397B4B6541dfFaEcC539BfF0c59",
      USDT: "0x66e428c3f687a1f3b6aa9b8c75e5b4c8c5e5b4c8", // Example, verify
    },
    defaultStablecoin: "USDC",
  },
  ethereum: {
    id: 1,
    name: "Ethereum",
    nativeCurrency: {
      name: "ETH",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: {
      default: "https://eth.llamarpc.com",
      public: "https://rpc.ankr.com/eth",
      infura: process.env.REACT_APP_INFURA_KEY ? `https://mainnet.infura.io/v3/${process.env.REACT_APP_INFURA_KEY}` : null,
      alchemy: process.env.REACT_APP_ALCHEMY_KEY ? `https://eth-mainnet.g.alchemy.com/v2/${process.env.REACT_APP_ALCHEMY_KEY}` : null,
    },
    blockExplorerUrls: ["https://etherscan.io"],
    wrappedNativeToken: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", // WETH
    stablecoins: {
      USDC: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
      USDT: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
      DAI: "0x6B175474E89094C44Da98b954EedeAC495271d0F",
    },
    defaultStablecoin: "USDC",
  },
  bsc: {
    id: 56,
    name: "BNB Smart Chain",
    nativeCurrency: {
      name: "BNB",
      symbol: "BNB",
      decimals: 18,
    },
    rpcUrls: {
      default: "https://bsc-dataseed1.binance.org",
      public: "https://bsc-dataseed.binance.org",
      infura: null,
      alchemy: null,
    },
    blockExplorerUrls: ["https://bscscan.com"],
    wrappedNativeToken: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c", // WBNB
    stablecoins: {
      USDC: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d",
      USDT: "0x55d398326f99059fF775485246999027B3197955",
      BUSD: "0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56",
    },
    defaultStablecoin: "USDT",
  },
  polygon: {
    id: 137,
    name: "Polygon",
    nativeCurrency: {
      name: "MATIC",
      symbol: "MATIC",
      decimals: 18,
    },
    rpcUrls: {
      default: "https://polygon-rpc.com",
      public: "https://rpc.ankr.com/polygon",
      infura: process.env.REACT_APP_INFURA_KEY ? `https://polygon-mainnet.infura.io/v3/${process.env.REACT_APP_INFURA_KEY}` : null,
      alchemy: process.env.REACT_APP_ALCHEMY_KEY ? `https://polygon-mainnet.g.alchemy.com/v2/${process.env.REACT_APP_ALCHEMY_KEY}` : null,
    },
    blockExplorerUrls: ["https://polygonscan.com"],
    wrappedNativeToken: "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270", // WMATIC
    stablecoins: {
      USDC: "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174",
      USDT: "0xc2132D05D31c914a87C6611C10748AEb04B58e8F",
      DAI: "0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063",
    },
    defaultStablecoin: "USDC",
  },
  arbitrum: {
    id: 42161,
    name: "Arbitrum One",
    nativeCurrency: {
      name: "ETH",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: {
      default: "https://arb1.arbitrum.io/rpc",
      public: "https://rpc.ankr.com/arbitrum",
      infura: process.env.REACT_APP_INFURA_KEY ? `https://arbitrum-mainnet.infura.io/v3/${process.env.REACT_APP_INFURA_KEY}` : null,
      alchemy: process.env.REACT_APP_ALCHEMY_KEY ? `https://arb-mainnet.g.alchemy.com/v2/${process.env.REACT_APP_ALCHEMY_KEY}` : null,
    },
    blockExplorerUrls: ["https://arbiscan.io"],
    wrappedNativeToken: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1", // WETH
    stablecoins: {
      USDC: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", // Native USDC (higher liquidity, $76.68M pool)
      "USDC.e": "0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8", // Bridged USDC.e
      USDT: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9",
    },
    defaultStablecoin: "USDC", // Use Native USDC as default for Arbitrum (higher liquidity pool)
  },
  optimism: {
    id: 10,
    name: "Optimism",
    nativeCurrency: {
      name: "ETH",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: {
      default: "https://mainnet.optimism.io",
      public: "https://rpc.ankr.com/optimism",
      infura: process.env.REACT_APP_INFURA_KEY ? `https://optimism-mainnet.infura.io/v3/${process.env.REACT_APP_INFURA_KEY}` : null,
      alchemy: process.env.REACT_APP_ALCHEMY_KEY ? `https://opt-mainnet.g.alchemy.com/v2/${process.env.REACT_APP_ALCHEMY_KEY}` : null,
    },
    blockExplorerUrls: ["https://optimistic.etherscan.io"],
    wrappedNativeToken: "0x4200000000000000000000000000000000000006", // WETH
    stablecoins: {
      USDC: "0x7F5c764cBc14f9669B88837ca1490cCa17c31607",
      USDT: "0x94b008aA00579c1307B0EF2c499aD98a8ce58e58",
    },
    defaultStablecoin: "USDC",
  },
  avalanche: {
    id: 43114,
    name: "Avalanche",
    nativeCurrency: {
      name: "AVAX",
      symbol: "AVAX",
      decimals: 18,
    },
    rpcUrls: {
      default: "https://api.avax.network/ext/bc/C/rpc",
      public: "https://rpc.ankr.com/avalanche",
      infura: null,
      alchemy: null,
    },
    blockExplorerUrls: ["https://snowtrace.io"],
    wrappedNativeToken: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7", // WAVAX
    stablecoins: {
      USDC: "0xB97EF9Ef8734C71904D8002F8b6B66D5572e0B0E",
      USDT: "0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7",
    },
    defaultStablecoin: "USDC",
  },
};

export const DEX_CONFIGS = {
  // Uniswap V2 (Ethereum, Polygon, Arbitrum, Optimism, Avalanche)
  uniswapV2: {
    name: "Uniswap V2",
    chains: ["ethereum", "polygon", "arbitrum", "optimism", "avalanche"],
    router: {
      ethereum: "0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D",
      polygon: "0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D",
      arbitrum: "0x4752ba5dbc23f44d87826276bf6fd6b1c372ad24", // SushiSwap on Arbitrum uses Uniswap V2
      optimism: "0x4752ba5dbc23f44d87826276bf6fd6b1c372ad24",
      avalanche: "0xE54Ca86531e17Ef3616d22Ca28b0D458b6C89106", // Pangolin
    },
    factory: {
      ethereum: "0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6F",
      polygon: "0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6F",
      arbitrum: "0xc35DADB65012eC5796536bD9864eD8773aBc74C4", // SushiSwap
      optimism: "0xc35DADB65012eC5796536bD9864eD8773aBc74C4",
      avalanche: "0xefa94DE7a4656D787667C749f7E6223D72C3cF96", // Pangolin
    },
  },
  // PancakeSwap (BSC)
  pancakeswap: {
    name: "PancakeSwap",
    chains: ["bsc"],
    router: {
      bsc: "0x10ED43C718714eb63d5aA57B78B54704E256024E",
    },
    factory: {
      bsc: "0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73",
    },
  },
  // VVS Finance (Cronos)
  vvs: {
    name: "VVS Finance",
    chains: ["cronos"],
    router: {
      cronos: "0x145863Eb42Cf62847A6Ca784e6416C1682b1b2Ae",
    },
    factory: {
      cronos: "0x3B44B2a187a7b3824131F8db5a74194D0a42Fc15",
    },
  },
  // SushiSwap (Multiple chains)
  sushiswap: {
    name: "SushiSwap",
    chains: ["ethereum", "polygon", "arbitrum", "avalanche"],
    router: {
      ethereum: "0xd9e1cE17f2641f24aE83637ab66a2cca9C378B9F",
      polygon: "0x1b02dA8Cb0d097eB8D57A175b88c7D8b47997506",
      arbitrum: "0x1b02dA8Cb0d097eB8D57A175b88c7D8b47997506",
      avalanche: "0x1b02dA8Cb0d097eB8D57A175b88c7D8b47997506",
    },
    factory: {
      ethereum: "0xC0AEe478e3658e2610c5F7A4A2E1777cE9e4f2Ac",
      polygon: "0xc35DADB65012eC5796536bD9864eD8773aBc74C4",
      arbitrum: "0xc35DADB65012eC5796536bD9864eD8773aBc74C4",
      avalanche: "0xc35DADB65012eC5796536bD9864eD8773aBc74C4",
    },
  },
  // QuickSwap (Polygon)
  quickswap: {
    name: "QuickSwap",
    chains: ["polygon"],
    router: {
      polygon: "0xa5E0829CaCEd8fFDD4De3c43696c57F7D7A678ff",
    },
    factory: {
      polygon: "0x5757371414417b8C6CAad45bAeF941aBc7d3Ab32",
    },
  },
  // Uniswap V3 (Multiple chains - 0.05% fee pools)
  uniswapV3: {
    name: "Uniswap V3",
    chains: ["ethereum", "polygon", "arbitrum", "optimism", "avalanche"],
    router: {
      ethereum: "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45",
      polygon: "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45",
      // Use Universal Router for Arbitrum to support both V3 and V4 pools
      arbitrum: "0xA51afAFe0263b40EdaEf0Df8781eA9aa03E381a3",
      optimism: "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45",
      avalanche: "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45",
    },
    factory: {
      ethereum: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
      polygon: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
      arbitrum: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
      optimism: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
      avalanche: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
    },
    // V3 uses fee tiers: 500 (0.05%), 3000 (0.3%), 10000 (1%)
    // Default to 0.05% (500) for lowest fees
    defaultFeeTier: 500,
  },
  // Uniswap V4 (Arbitrum - uses Universal Router)
  uniswapV4: {
    name: "Uniswap V4",
    chains: ["arbitrum"],
    router: {
      arbitrum: "0xA51afAFe0263b40EdaEf0Df8781eA9aa03E381a3", // Universal Router
    },
    factory: {
      arbitrum: "0x360E68faCcca8cA495c1B759Fd9EEe466db9FB32", // PoolManager
    },
    // V4 uses fee tiers similar to V3
    defaultFeeTier: 500,
  },
};

/**
 * Get router address for a chain + DEX combination
 */
export function getRouterAddress(chainId, dexId) {
  const chain = Object.keys(CHAIN_CONFIGS).find(
    (k) => CHAIN_CONFIGS[k].id === chainId || k === chainId
  );
  if (!chain) {
    throw new Error(`Chain not found: ${chainId}`);
  }

  const dex = DEX_CONFIGS[dexId];
  if (!dex) {
    throw new Error(`DEX not found: ${dexId}`);
  }

  if (!dex.chains.includes(chain)) {
    throw new Error(`DEX ${dexId} not supported on chain ${chain}`);
  }

  const router = dex.router[chain];
  if (!router) {
    throw new Error(`Router address not found for ${dexId} on ${chain}`);
  }

  return router;
}

/**
 * Get factory address for a chain + DEX combination
 */
export function getFactoryAddress(chainId, dexId) {
  const chain = Object.keys(CHAIN_CONFIGS).find(
    (k) => CHAIN_CONFIGS[k].id === chainId || k === chainId
  );
  if (!chain) {
    throw new Error(`Chain not found: ${chainId}`);
  }

  const dex = DEX_CONFIGS[dexId];
  if (!dex) {
    throw new Error(`DEX not found: ${dexId}`);
  }

  if (!dex.chains.includes(chain)) {
    throw new Error(`DEX ${dexId} not supported on chain ${chain}`);
  }

  const factory = dex.factory[chain];
  if (!factory) {
    throw new Error(`Factory address not found for ${dexId} on ${chain}`);
  }

  return factory;
}

/**
 * Get RPC URL for a chain
 */
export function getRpcUrl(chainId, preferPrivate = false) {
  const chain = Object.keys(CHAIN_CONFIGS).find(
    (k) => CHAIN_CONFIGS[k].id === chainId || k === chainId
  );
  if (!chain) {
    throw new Error(`Chain not found: ${chainId}`);
  }

  const config = CHAIN_CONFIGS[chain];
  if (preferPrivate && config.rpcUrls.infura) {
    return config.rpcUrls.infura;
  }
  if (preferPrivate && config.rpcUrls.alchemy) {
    return config.rpcUrls.alchemy;
  }
  return config.rpcUrls.default || config.rpcUrls.public;
}

/**
 * Get wrapped native token address for a chain
 */
export function getWrappedNativeToken(chainId) {
  const chain = Object.keys(CHAIN_CONFIGS).find(
    (k) => CHAIN_CONFIGS[k].id === chainId || k === chainId
  );
  if (!chain) {
    throw new Error(`Chain not found: ${chainId}`);
  }

  return CHAIN_CONFIGS[chain].wrappedNativeToken;
}

/**
 * Get stablecoin address for a chain
 */
export function getStablecoinAddress(chainId, stablecoinSymbol = null) {
  const chain = Object.keys(CHAIN_CONFIGS).find(
    (k) => CHAIN_CONFIGS[k].id === chainId || k === chainId
  );
  if (!chain) {
    throw new Error(`Chain not found: ${chainId}`);
  }

  const config = CHAIN_CONFIGS[chain];
  const symbol = stablecoinSymbol || config.defaultStablecoin;
  const address = config.stablecoins[symbol];
  
  if (!address) {
    throw new Error(`Stablecoin ${symbol} not found on chain ${chain}`);
  }

  return address;
}

/**
 * Get available DEXes for a chain
 */
export function getAvailableDexes(chainId) {
  const chain = Object.keys(CHAIN_CONFIGS).find(
    (k) => CHAIN_CONFIGS[k].id === chainId || k === chainId
  );
  if (!chain) {
    return [];
  }

  const available = Object.keys(DEX_CONFIGS).filter((dexId) =>
    DEX_CONFIGS[dexId].chains.includes(chain)
  );
  
  // CRITICAL FIX: Prioritize uniswapV3 for Arbitrum (better liquidity, lower fees)
  // Put it first in the list so it becomes the default selection
  if (chain === "arbitrum" && available.includes("uniswapV3")) {
    return ["uniswapV3", ...available.filter(d => d !== "uniswapV3")];
  }
  
  return available;
}

/**
 * Validate chain + DEX combination
 */
export function validateChainDex(chainId, dexId) {
  try {
    getRouterAddress(chainId, dexId);
    getFactoryAddress(chainId, dexId);
    return { valid: true, error: null };
  } catch (error) {
    return { valid: false, error: error.message };
  }
}

/**
 * Get chain config by ID or name
 */
export function getChainConfig(chainId) {
  const chain = Object.keys(CHAIN_CONFIGS).find(
    (k) => CHAIN_CONFIGS[k].id === chainId || k === chainId
  );
  if (!chain) {
    throw new Error(`Chain not found: ${chainId}`);
  }
  return { ...CHAIN_CONFIGS[chain], key: chain };
}

/**
 * Get all supported chains
 */
export function getAllChains() {
  return Object.keys(CHAIN_CONFIGS).map((key) => ({
    key,
    ...CHAIN_CONFIGS[key],
  }));
}

/**
 * Get all supported DEXes
 */
export function getAllDexes() {
  return Object.keys(DEX_CONFIGS).map((key) => ({
    key,
    ...DEX_CONFIGS[key],
  }));
}

